// src/pages/GroupDetailPage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { THEME_COLORS } from '../styles/theme';
import { buildInviteContent, buildWhatsAppLink } from '../features/collaboration/services/inviteLinkService';
import { sendInviteEmail } from '../features/collaboration/services/inviteEmailService';
import {
  fetchGroupById,
  fetchGroupMembers,
  fetchGroupMembership,
  fetchGroupJoinRequests,
  respondToJoinRequest,
  fetchConnections,
  sendGroupInvite,
  joinGroupViaLink,
  requestToJoinGroup
} from '../features/collaboration/services/collaborationService';

const GroupDetailPage = () => {
  const { currentUser, userProfile } = useAuth();
  const { groupId } = useParams();

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [membership, setMembership] = useState(null);
  const [requests, setRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteWhatsApp, setInviteWhatsApp] = useState('');

  const canManage = useMemo(
    () => ['owner', 'admin'].includes(membership?.role),
    [membership]
  );

  useEffect(() => {
    const loadGroup = async () => {
      if (!currentUser || !groupId) return;
      setLoading(true);
      try {
        const [groupData, memberData, membershipData, connectionData] = await Promise.all([
          fetchGroupById(groupId),
          fetchGroupMembers(groupId),
          fetchGroupMembership(groupId, currentUser.uid),
          fetchConnections(currentUser.uid)
        ]);
        setGroup(groupData);
        setMembers(memberData || []);
        setMembership(membershipData);
        setConnections((connectionData || []).filter((conn) => conn.status === 'active'));
        if (membershipData && ['owner', 'admin'].includes(membershipData.role)) {
          const joinRequests = await fetchGroupJoinRequests(groupId);
          setRequests(joinRequests || []);
        } else {
          setRequests([]);
        }
      } catch (error) {
        console.error('Error loading group:', error);
        setNotice({ type: 'error', message: error.message || 'Failed to load group.' });
      } finally {
        setLoading(false);
      }
    };

    loadGroup();
  }, [currentUser, groupId]);

  const refreshMembers = async () => {
    const updated = await fetchGroupMembers(groupId);
    setMembers(updated || []);
    const membershipData = await fetchGroupMembership(groupId, currentUser.uid);
    setMembership(membershipData);
  };

  const handleInvite = async (userId) => {
    try {
      await sendGroupInvite(groupId, currentUser.uid, userId, inviteMessage);
      setNotice({ type: 'success', message: 'Invite sent.' });
      setInviteOpen(false);
      setInviteMessage('');
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to send invite.' });
    }
  };

  const inviterName = userProfile?.name || currentUser?.displayName || 'Teachmate';
  const groupLink = group
    ? `${window.location.origin}/groups/${group.id}${group.joinCode ? `?code=${group.joinCode}` : ''}`
    : '';
  const inviteBody = buildInviteContent({
    type: 'group',
    inviterName,
    groupName: group?.name,
    link: groupLink,
    note: inviteMessage
  });
  const inviteSubject = `${inviterName} invited you to join ${group?.name || 'a Teachmate group'}`;
  const emailValid = !inviteEmail || /\S+@\S+\.\S+/.test(inviteEmail);
  const whatsappValid = !inviteWhatsApp || /[0-9]/.test(inviteWhatsApp);

  const handleCopyLink = async () => {
    if (!group?.joinCode) return;
    const link = `${window.location.origin}/groups/${group.id}?code=${group.joinCode}`;
    try {
      await navigator.clipboard.writeText(link);
      setNotice({ type: 'success', message: 'Group link copied.' });
    } catch (error) {
      setNotice({ type: 'error', message: 'Failed to copy link.' });
    }
  };

  const handleJoin = async () => {
    try {
      await joinGroupViaLink(groupId, currentUser.uid, group?.joinCode || '');
      await refreshMembers();
      setNotice({ type: 'success', message: 'You joined the group.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to join group.' });
    }
  };

  const handleRequestJoin = async () => {
    try {
      await requestToJoinGroup(groupId, currentUser.uid, requestMessage);
      setRequestMessage('');
      setNotice({ type: 'success', message: 'Join request submitted.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to request to join.' });
    }
  };

  const handleRespondRequest = async (userId, approve) => {
    try {
      await respondToJoinRequest(groupId, userId, approve, currentUser.uid);
      const updated = await fetchGroupJoinRequests(groupId);
      setRequests(updated || []);
      if (approve) await refreshMembers();
      setNotice({ type: 'success', message: approve ? 'Request approved.' : 'Request declined.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to respond to request.' });
    }
  };

  const renderFilters = () => {
    if (!group?.filters) return null;
    const filterItems = [];
    if (group.filters.subjects?.length) filterItems.push(`Subjects: ${group.filters.subjects.join(', ')}`);
    if (group.filters.grades?.length) filterItems.push(`Grades: ${group.filters.grades.join(', ')}`);
    if (group.filters.schools?.length) filterItems.push(`Schools: ${group.filters.schools.join(', ')}`);
    if (group.filters.districts?.length) filterItems.push(`Districts: ${group.filters.districts.join(', ')}`);
    if (group.filters.projects?.length) filterItems.push(`Projects: ${group.filters.projects.join(', ')}`);
    if (filterItems.length === 0) return null;

    return (
      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
        {filterItems.map((item) => (
          <Chip key={item} label={item} size="small" />
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
        <Typography color="textSecondary">Loading group...</Typography>
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
        <Typography color="textSecondary">Group not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
      <Typography variant="h4" fontWeight="bold" color={THEME_COLORS.primary} mb={2}>
        {group.name}
      </Typography>

      {notice && (
        <Alert severity={notice.type} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1.3fr 1fr' }} gap={3}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              Purpose
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              {group.purpose || 'No purpose provided.'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {group.visibility} • {group.joinPolicy}
            </Typography>
            {renderFilters()}

            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              {group.allowLinkJoin && canManage && (
                <Button size="small" onClick={handleCopyLink}>
                  Copy Join Link
                </Button>
              )}
              {!membership && group.joinPolicy === 'open' && group.allowLinkJoin && (
                <Button size="small" variant="contained" onClick={handleJoin}>
                  Join Group
                </Button>
              )}
              {!membership && group.joinPolicy === 'request' && (
                <Button size="small" variant="outlined" onClick={handleRequestJoin}>
                  Request to Join
                </Button>
              )}
              {membership && (
                <Chip label={`Role: ${membership.role}`} size="small" />
              )}
            </Box>
            {group.joinPolicy === 'request' && !membership && (
              <TextField
                label="Request message (optional)"
                fullWidth
                multiline
                minRows={2}
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Members ({members.length})
            </Typography>
            <List dense>
              {members.map((member) => (
                <ListItem key={member.userId}>
                  <ListItemAvatar>
                    <Avatar src={member.user?.profileImage || undefined}>
                      {(member.user?.name || 'U').charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.user?.name || 'User'}
                    secondary={member.role}
                  />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            {canManage && (
              <Button variant="contained" onClick={() => setInviteOpen(true)}>
                Invite Contacts
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>

      {canManage && (
        <Card elevation={2} sx={{ borderRadius: 2, mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Pending Requests
            </Typography>
            {requests.length === 0 ? (
              <Typography color="textSecondary">No pending requests.</Typography>
            ) : (
              requests.map((request) => (
                <Box key={request.userId} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {request.user?.name || 'User'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {request.user?.title || ''}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Button size="small" variant="contained" onClick={() => handleRespondRequest(request.userId, true)}>
                      Approve
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleRespondRequest(request.userId, false)}>
                      Decline
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
          setInviteMessage('');
          setInviteEmail('');
          setInviteWhatsApp('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Invite Contacts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Invite by Email or WhatsApp
            </Typography>
            <TextField
              label="Email address (optional)"
              fullWidth
              size="small"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              error={!emailValid}
              helperText={!emailValid ? 'Enter a valid email address.' : ' '}
              sx={{ mb: 1 }}
            />
            <TextField
              label="WhatsApp number (optional)"
              fullWidth
              size="small"
              value={inviteWhatsApp}
              onChange={(e) => setInviteWhatsApp(e.target.value)}
              error={!whatsappValid}
              helperText={!whatsappValid ? 'Enter a valid phone number.' : ' '}
              sx={{ mb: 1 }}
            />
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                disabled={!inviteEmail.trim() || !emailValid}
                onClick={async () => {
                  if (!inviteEmail.trim() || !emailValid) return;
                  try {
                    await sendInviteEmail({
                      toEmail: inviteEmail.trim(),
                      subject: inviteSubject,
                      body: inviteBody,
                      type: 'group',
                      link: groupLink,
                      groupId: group?.id || null,
                      groupName: group?.name || null
                    });
                    setNotice({ type: 'success', message: 'Email invite sent.' });
                  } catch (error) {
                    console.error('Error sending invite email:', error);
                    setNotice({ type: 'error', message: error.message || 'Failed to send email invite.' });
                  }
                }}
              >
                Send Email Invite
              </Button>
              <Button
                variant="outlined"
                disabled={!inviteWhatsApp.trim() || !whatsappValid}
                onClick={() => {
                  const url = buildWhatsAppLink(inviteWhatsApp, inviteBody);
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                Send WhatsApp Invite
              </Button>
            </Box>
          </Box>
          <TextField
            label="Invite message (optional)"
            fullWidth
            multiline
            minRows={2}
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            sx={{ mb: 2 }}
          />
          {connections.length === 0 ? (
            <Typography color="textSecondary">No active connections.</Typography>
          ) : (
            <List>
              {connections.map((connection) => (
                <ListItem key={connection.id} secondaryAction={
                  <Button size="small" onClick={() => handleInvite(connection.user?.id)}>
                    Invite
                  </Button>
                }>
                  <ListItemAvatar>
                    <Avatar src={connection.user?.profileImage || undefined}>
                      {(connection.user?.name || 'U').charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={connection.user?.name || 'User'}
                    secondary={connection.user?.title || ''}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInviteOpen(false);
              setInviteMessage('');
              setInviteEmail('');
              setInviteWhatsApp('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDetailPage;
